import { AngleRightIcon, Box, CardContent, CardHeader, Divider, EllipsisVIcon, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, IconButton, LabelValueList, SizeContextProvider, SpinnerButton, makeStyles, useTheme, } from '../..';
import { useCardTable } from './Context';
import { Checkbox, Popover } from '@material-ui/core';
import { capitalize, lowerCase, values } from 'lodash';
import { checkboxColumnDef, leftDesktopPadding } from './Shared';
import React, { Fragment, useRef, useState } from 'react';
const useStyles = makeStyles(({ spacing, palette }) => ({
    expansionPanelSummary: (props) => ({
        paddingLeft: props.isMobile ? 0 : spacing(leftDesktopPadding),
        paddingRight: props.isMobile ? 0 : spacing(3),
        minHeight: '47px',
        justifyContent: 'space-between',
    }),
    expansionPanelRoot: (props) => {
        const { highlightColor } = props;
        const style = {
            border: `1px solid ${palette.divider}`,
            borderRadius: props.hasSubRow ? '4px 4px 0 0' : '4px',
            borderBottomWidth: props.hasSubRow ? '0' : '1px',
            marginBottom: props.hasSubRow ? '0' : `${spacing(0.5)}px !important`,
        };
        if (highlightColor) {
            return {
                ...style,
                borderLeftColor: `${highlightColor} !important`,
                borderLeftWidth: `3px !important`,
            };
        }
        return style;
    },
    expansionPanelRootExpanded: {
        borderBottom: `1px solid ${palette.divider}`,
    },
    summaryRoot: {
        minHeight: '47px !important',
    },
    summaryExpanded: {
        marginTop: '0px !important',
        marginBottom: '0px !important',
    },
    rowSpan: {
        display: 'flex',
        alignItems: 'center',
        overflow: 'ellipsis',
        paddingRight: spacing(1),
        '& > *': {
            marginTop: 'unset',
            marginBottom: 'unset',
            width: '100%',
        },
    },
    detailsRoot: {
        backgroundColor: 'unset',
    },
    cardHeaderRoot: {
        borderBottom: 'none',
        width: '100%',
    },
    expandedContentContainer: {
        display: 'flex',
    },
    hiddenColumnsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    hiddenColumn: {
        marginRight: spacing(2),
        flex: '0 45%',
    },
}));
const ActionsButton = ({ actionsMenu, isMobile }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const handleClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setOpen(true);
    };
    return (React.createElement("div", { ref: ref },
        React.createElement(Box, { display: "flex", justifyContent: "center", alignItems: "center", height: "100%" },
            React.createElement(IconButton, { onClick: handleClick, edge: isMobile ? false : 'end' },
                React.createElement(EllipsisVIcon, null)),
            React.createElement(Popover, { elevation: 4, open: open, onClose: () => setOpen(false), anchorEl: ref.current, anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                } },
                React.createElement("div", { onClick: () => setOpen(false) }, actionsMenu)))));
};
const ExpandIcon = ({ isExpanded, onClick, isMobile }) => {
    const theme = useTheme();
    const transition = {
        duration: theme.transitions.duration.shortest,
    };
    return (React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "center" },
        React.createElement(IconButton, { edge: isMobile ? false : 'end', style: {
                transform: `rotate(${isExpanded ? 90 : 0}deg)`,
                transition: theme.transitions.create('transform', transition),
            }, onClick: e => {
                e.preventDefault();
                onClick();
            } },
            React.createElement(AngleRightIcon, null))));
};
export const CardTableRow = ({ columns, id, link, children, highlightColor, actionsMenu, subRow, }) => {
    const { colWidths, expandedRows, setRowExpanded, visibleColumns, hiddenColumns, isMobile, isResizing, selectionMapping, onSelectionChange, } = useCardTable();
    const theme = useTheme();
    const styleProps = { isMobile, highlightColor, hasSubRow: !!subRow };
    const classes = useStyles(styleProps);
    const [mobileExpanded, setMobileExpanded] = useState(false);
    const isExpanded = expandedRows.includes(id);
    const isPanelDesktopExpandable = !!children || !!hiddenColumns.length;
    const handleToggleExpand = () => {
        if (isMobile)
            setMobileExpanded(!mobileExpanded);
        setRowExpanded(id, !isExpanded);
    };
    const checkStateToBooleanMapping = {
        checked: true,
        unchecked: false,
        indeterminate: undefined,
    };
    if (selectionMapping) {
        const selectionState = selectionMapping && selectionMapping[id] ? checkStateToBooleanMapping[selectionMapping[id].state] : null;
        const onChange = (event, checked) => {
            if (!onSelectionChange)
                return;
            const state = checked ? 'checked' : 'unchecked';
            const delta = {};
            delta[id] = {
                ...selectionMapping[id],
                state,
            };
            onSelectionChange(delta);
        };
        columns[checkboxColumnDef.name] = (React.createElement(Checkbox, { checked: selectionState ? selectionState : false, edge: "start", onChange: onChange }));
    }
    if (isMobile) {
        const columnsToShow = visibleColumns.slice(0, selectionMapping ? 2 : 1);
        const otherColumns = visibleColumns.slice(columnsToShow.length);
        const dataList = otherColumns.map(({ name, label }) => {
            const node = columns[name];
            return {
                label: label || capitalize(lowerCase(name)),
                value: node,
                isEmpty: !node,
            };
        });
        const handleShowMore = () => {
            setMobileExpanded(true);
        };
        const title = (React.createElement(Box, { display: "flex", alignItems: "center" }, columnsToShow.map(e => columns[e.name])));
        return (React.createElement(React.Fragment, null,
            React.createElement(ExpansionPanel, { expanded: isExpanded, classes: { root: classes.expansionPanelRoot, expanded: classes.expansionPanelRootExpanded } },
                React.createElement(ExpansionPanelSummary, { classes: {
                        root: classes.summaryRoot,
                        content: classes.expansionPanelSummary,
                        expanded: classes.summaryExpanded,
                    }, IconButtonProps: { style: { marginRight: 'unset' } }, style: isMobile && { marginLeft: '0px !important' } },
                    React.createElement(Box, { display: "flex" },
                        React.createElement(CardHeader, { link: link, title: title, className: classes.cardHeaderRoot })),
                    React.createElement(Box, { display: "flex", alignItems: "stretch" },
                        React.createElement(ExpandIcon, { onClick: handleToggleExpand, isExpanded: isExpanded, isMobile: true }),
                        actionsMenu && (React.createElement(Fragment, null,
                            React.createElement(Box, { display: "flex", alignItems: "center" },
                                React.createElement(Divider, { orientation: "vertical", style: { height: '50%' } })),
                            React.createElement(ActionsButton, { actionsMenu: actionsMenu, isMobile: true }))))),
                React.createElement(ExpansionPanelDetails, { classes: { root: classes.detailsRoot } },
                    React.createElement(CardContent, { surface: "light" },
                        React.createElement(SizeContextProvider, { value: { size: 'small' } },
                            React.createElement(LabelValueList, { items: dataList }),
                            !!children && (React.createElement(SpinnerButton, { variant: "contained", color: "primary", onClick: handleShowMore, fullWidth: true, loading: false, caption: "Show More" }))),
                        React.createElement(Box, { mt: 2, className: classes.expandedContentContainer }, mobileExpanded && children)))),
            subRow));
    }
    const allWidthsZero = values(colWidths).every(width => width === 0);
    const expansionPanelSummaryProps = {
        classes: {
            root: classes.summaryRoot,
            content: classes.expansionPanelSummary,
            expanded: classes.summaryExpanded,
        },
        IconButtonProps: { style: { marginRight: 'unset' } },
        ...(link && {
            component: 'button',
            href: link,
        }),
    };
    return !isResizing ? (React.createElement(React.Fragment, null,
        React.createElement(ExpansionPanel, { expanded: isExpanded, classes: { root: classes.expansionPanelRoot, expanded: classes.expansionPanelRootExpanded } },
            React.createElement(ExpansionPanelSummary, Object.assign({}, expansionPanelSummaryProps),
                React.createElement(Box, { display: "flex" },
                    React.createElement(SizeContextProvider, { value: { size: 'small' } }, !allWidthsZero &&
                        visibleColumns.map(({ name }) => {
                            const node = columns[name];
                            return (React.createElement("span", { className: classes.rowSpan, key: name, style: { width: colWidths[name] } }, node));
                        }))),
                React.createElement(Box, { display: "flex", alignItems: "stretch" },
                    isPanelDesktopExpandable && (React.createElement(ExpandIcon, { onClick: handleToggleExpand, isExpanded: isExpanded, isMobile: false })),
                    isPanelDesktopExpandable && actionsMenu && (React.createElement(Box, { display: "flex", alignItems: "center", marginLeft: `${theme.spacing(2)}px` },
                        React.createElement(Divider, { orientation: "vertical", style: { height: '50%' } }))),
                    actionsMenu && React.createElement(ActionsButton, { actionsMenu: actionsMenu, isMobile: false }))),
            isPanelDesktopExpandable && !isResizing && (React.createElement(ExpansionPanelDetails, { classes: { root: classes.detailsRoot } },
                React.createElement(CardContent, { surface: "light" },
                    !!hiddenColumns.length && (React.createElement("div", { className: classes.hiddenColumnsContainer }, hiddenColumns.map(({ name, label }) => {
                        const node = columns[name];
                        return (React.createElement("div", { className: classes.hiddenColumn, key: name },
                            React.createElement("h4", null, label),
                            node));
                    }))),
                    React.createElement(Box, { mt: 2, className: classes.expandedContentContainer }, isExpanded && children))))),
        subRow)) : null;
};
//# sourceMappingURL=Row.js.map